import { useCallback } from 'react'
import { gql } from '@apollo/client'
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react'
import {
  GooglePlaySubscriptionCancelMutation,
  GooglePlaySubscriptionCancelMutationVariables,
  RokuSubscriptionCancelMutation,
  RokuSubscriptionCancelMutationVariables,
  UserSubscriptionsQuery,
} from '@/types/codegen-federation'

export const getUserSubscriptions = gql`
  query userSubscriptions($includeGifts: Boolean!) {
    user {
      name
      id
      subscriptions(includeGifts: $includeGifts) {
        currency
        expiresAt
        id
        managementUrl
        name
        originalPurchaseAt
        period
        price
        isGift
        purchasedAt
        source
        sourceId
        unsubscribedAt
        updatedAt
        store
        storeTransactionId
      }
    }
  }
`

export const useUserSubscriptions = (includeGifts: boolean = true): useQuery.Result<UserSubscriptionsQuery> => {
  const client = useApolloClient()
  const results = useQuery<UserSubscriptionsQuery>(getUserSubscriptions, {
    client,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    refetchWritePolicy: 'overwrite',
    variables: { includeGifts },
  })

  return results
}

export const useMemberships = () => {
  return []
}

const CANCEL_ROKU_SUBSCRIPTION_MUTATION = gql`
  mutation RokuSubscriptionCancel($input: CancelRokuSubscriptionInput!) {
    rokuSubscriptionCancel(input: $input) {
      success
    }
  }
`

export const useCancelRokuSubscription = () => {
  const client = useApolloClient()
  const [mutate] = useMutation<RokuSubscriptionCancelMutation, RokuSubscriptionCancelMutationVariables>(
    CANCEL_ROKU_SUBSCRIPTION_MUTATION,
    {
      client,
    },
  )

  const cancelRokuSubscription = useCallback(
    async (storeTransactionId: string) => {
      const { data } = await mutate({ variables: { input: { transactionId: storeTransactionId } } })
      return data?.rokuSubscriptionCancel?.success
    },
    [mutate],
  )

  return { cancelRokuSubscription }
}

const CANCEL_GOOGLE_PLAY_SUBSCRIPTION_MUTATION = gql`
  mutation GooglePlaySubscriptionCancel($transactionId: String!, $userId: String!) {
    googlePlaySubscriptionCancel(transactionId: $transactionId, userId: $userId) {
      error
      success
    }
  }
`

export const useCancelGooglePlaySubscription = () => {
  const client = useApolloClient()
  const [mutate] = useMutation<GooglePlaySubscriptionCancelMutation, GooglePlaySubscriptionCancelMutationVariables>(
    CANCEL_GOOGLE_PLAY_SUBSCRIPTION_MUTATION,
    {
      client,
    },
  )

  const cancelGooglePlaySubscription = useCallback(
    async (transactionId: string, userId: string) => {
      const { data } = await mutate({
        variables: {
          transactionId,
          userId,
        },
      })
      return {
        success: data?.googlePlaySubscriptionCancel?.success,
        error: data?.googlePlaySubscriptionCancel?.error,
      }
    },
    [mutate],
  )

  return { cancelGooglePlaySubscription }
}
